export type KeyState = 'idle' | 'pressed' | 'held' | 'released';

export class InputManager {
  private keyStates = new Map<string, KeyState>();
  private keyDownSet = new Set<string>();
  private keyUpSet = new Set<string>();
  private textInput: string[] = [];

  constructor(private element: Window & typeof globalThis = window) {
    this.element.addEventListener('keydown', this.onKeyDown);
    this.element.addEventListener('keyup', this.onKeyUp);
  }

  destroy(): void {
    this.element.removeEventListener('keydown', this.onKeyDown);
    this.element.removeEventListener('keyup', this.onKeyUp);
  }

  update(): void {
    this.keyDownSet.forEach((key) => {
      const current = this.keyStates.get(key);
      if (current === 'pressed' || current === 'held') {
        this.keyStates.set(key, 'held');
      } else {
        this.keyStates.set(key, 'pressed');
      }
    });

    this.keyUpSet.forEach((key) => {
      this.keyStates.set(key, 'released');
    });

    for (const [key, state] of this.keyStates) {
      if (state === 'released') {
        this.keyStates.set(key, 'idle');
      }
    }

    this.keyDownSet.clear();
    this.keyUpSet.clear();
  }

  getState(key: string): KeyState {
    return this.keyStates.get(key) ?? 'idle';
  }

  isPressed(key: string): boolean {
    return this.getState(key) === 'pressed';
  }

  isHeld(key: string): boolean {
    const state = this.getState(key);
    return state === 'pressed' || state === 'held';
  }

  isReleased(key: string): boolean {
    return this.getState(key) === 'released';
  }

  consumeTextInput(): string[] {
    const buffered = [...this.textInput];
    this.textInput = [];
    return buffered;
  }

  private onKeyDown = (event: KeyboardEvent): void => {
    const key = event.key;
    if (!event.repeat) {
      this.keyDownSet.add(key.toLowerCase());
    }
    if (!event.ctrlKey && !event.metaKey && !event.altKey) {
      if (key.length === 1 || key === 'Backspace' || key === 'Enter' || key === ' ') {
        this.textInput.push(key);
      }
    }
    event.preventDefault();
  };

  private onKeyUp = (event: KeyboardEvent): void => {
    this.keyDownSet.delete(event.key.toLowerCase());
    this.keyUpSet.add(event.key.toLowerCase());
    event.preventDefault();
  };
}
